/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetHooksStatusRequest,
  GetHooksStatusSecurity,
} from "../models/operations/gethooksstatus.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetHooksStatusQuery,
  GetHooksStatusQueryData,
  prefetchGetHooksStatus,
  queryKeyGetHooksStatus,
} from "./getHooksStatus.core.js";
export {
  buildGetHooksStatusQuery,
  type GetHooksStatusQueryData,
  prefetchGetHooksStatus,
  queryKeyGetHooksStatus,
};

export type GetHooksStatusQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getStatus hooks
 *
 * @remarks
 * Report whether the organization has any hook telemetry source configured. Reads configuration only; it does not query hook traffic.
 */
export function useGetHooksStatus(
  request?: GetHooksStatusRequest | undefined,
  security?: GetHooksStatusSecurity | undefined,
  options?: QueryHookOptions<GetHooksStatusQueryData, GetHooksStatusQueryError>,
): UseQueryResult<GetHooksStatusQueryData, GetHooksStatusQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGetHooksStatusQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getStatus hooks
 *
 * @remarks
 * Report whether the organization has any hook telemetry source configured. Reads configuration only; it does not query hook traffic.
 */
export function useGetHooksStatusSuspense(
  request?: GetHooksStatusRequest | undefined,
  security?: GetHooksStatusSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetHooksStatusQueryData,
    GetHooksStatusQueryError
  >,
): UseSuspenseQueryResult<GetHooksStatusQueryData, GetHooksStatusQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetHooksStatusQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetHooksStatusData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: GetHooksStatusQueryData,
): GetHooksStatusQueryData | undefined {
  const key = queryKeyGetHooksStatus(...queryKeyBase);

  return client.setQueryData<GetHooksStatusQueryData>(key, data);
}

export function invalidateGetHooksStatus(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "hooks", "getStatus", ...queryKeyBase],
  });
}

export function invalidateAllGetHooksStatus(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "hooks", "getStatus"],
  });
}
